"use client";

import type React from "react";
import { useEffect, useState } from "react";
import { useHistory, Link, useLocation } from "react-router-dom";
import {
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Button,
  Divider,
  Box,
  LinearProgress,
  Paper,
  Avatar,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  ArrowBack,
  Download,
  InsertDriveFile,
  CalendarToday,
  AccessTime,
  LocationOn,
  Phone,
  Person,
  Description,
} from "@mui/icons-material";
// import { getLeaveApplication } from "../../Services/LeaveService"
// import LeaveStatusActions from "../../components/leave/LeaveStatusActions"
import styles from "./LeaveDetailPage.module.css";
import LeaveStatusActions from "../../../components/leave/LeaveStatusActions";
import { getLeaveApplication } from "../../../api/LeaveService";
import { IonContent, IonPage } from "@ionic/react";
import { getUser, getUserData } from "../../../common/utils";

interface Document {
  name: string;
  url: string;
  type: string;
  size: number;
}

interface LeaveApplication {
  id: string;
  employeeName: string;
  employee_id: string;
  leave_type: string;
  start_date: string;
  end_date: string;
  reliefPersonName: string;
  contactInfo: string;
  location: string;
  department: string;
  reason: string;
  status: string;
  documents?: Document[];
  created_at: string;
  days_requested: number;
  daysRemaining: number;
}

const LeaveDetailPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const uselocation = useLocation();
  const queryParams = new URLSearchParams(uselocation.search);
  const leaveId = queryParams.get("id");

  const userRole = getUser()?.loginData

  console.log({userRole})

  const history = useHistory();
  const [leaveApplication, setLeaveApplication] =
    useState<LeaveApplication | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [remainingDays, setRemainingDays] = useState<number>(
    (uselocation.state as { remainingDays?: number })?.remainingDays ?? 30
  );
  const [totalDays, setTotalDays] = useState<number>(
    (uselocation.state as { totalDays?: number })?.totalDays ?? 30
  );

  useEffect(() => {
    const fetchLeaveApplication = async () => {
      try {
        if (leaveId) {
          const data = await getLeaveApplication(leaveId);
          setLeaveApplication(data);
        }
      } catch (err) {
        setError("Failed to load leave application details");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaveApplication();
  }, [leaveId]);

  if (loading) {
    return (
      <Box className={styles.loadingContainer}>
        <LinearProgress className={styles.loadingBar} />
        <Typography variant="body1" className={styles.loadingText}>
          Loading leave details...
        </Typography>
      </Box>
    );
  }

  if (error || !leaveApplication) {
    return (
      <Paper elevation={3} className={styles.errorContainer}>
        <Typography variant="h6" color="error" gutterBottom>
          {error || "Leave application not found"}
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={() => history.push("/folder/leave_page")}
          className={styles.errorButton}
        >
          Back to Leave Management

        </Button>
      </Paper>
    );
  }

  const {
    employeeName,
    employee_id,
    leave_type,
    start_date,
    end_date,
    reliefPersonName,
    contactInfo,
    location,
    department,
    reason,
    status,
    documents,
    created_at,
    days_requested,
    daysRemaining,
  } = leaveApplication;

  const getStatusColor = (status: string) => {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case "approved":
        return styles.statusApproved;
      case "rejected":
        return styles.statusRejected;
      case "pending":
        return styles.statusPending;
      default:
        return styles.statusDefault;
    }
  };

  const getStatusIcon = (status: string) => {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case "approved":
        return "✓";
      case "rejected":
        return "✕";
      case "pending":
        return "!";
      default:
        return "?";
    }
  };

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const usedDays = totalDays - remainingDays;
  const usagePercentage = (usedDays / totalDays) * 100;
//const history = useHistory();

       // history.push("/folder/view-whistle-blowing/view");
        const handleBack = () => {  
          history.push("/folder/view-leaves/View%20Leaves");
        };
  return (
    <IonPage className="pushDown">
      <IonContent>
        <div className={styles.pageContainer}>
          <Box className={styles.pageHeader}>
            <Button
              variant="text"
              startIcon={<ArrowBack />}
              onClick={handleBack}
              className={styles.backButton}
            >
              Back
            </Button>

           


            <Typography
              variant="h5"
              component="h1"
              className={styles.pageTitle}
            >
              Leave Application
            </Typography>
          </Box>

          <Box className={styles.statusBanner}>
            <div
              className={`${styles.statusIndicator} ${getStatusColor(
                status as string
              )}`}
            >
              <span className={styles.statusIcon}>
                {getStatusIcon(status as string)}
              </span>
              <Typography variant="body1" className={styles.statusText}>
                {typeof status === "string"
                  ? status.charAt(0).toUpperCase() + status.slice(1)
                  : "Unknown"}
              </Typography>
            </div>
            <Typography variant="body2" className={styles.submissionDate}>
              Submitted on {new Date(created_at).toLocaleDateString()}
            </Typography>
          </Box>

          <Grid container spacing={3} className={styles.contentGrid}>
            <Grid item xs={12} lg={8}>
              <Card elevation={0} className={styles.mainCard}>
                <CardHeader
                  title={
                    <Typography variant="h6" className={styles.cardTitle}>
                      Employee Information
                    </Typography>
                  }
                  className={styles.cardHeader}
                />
                <CardContent className={styles.cardContent}>
                  <Box className={styles.employeeSection}>
                    <Avatar className={styles.employeeAvatar}>
                      {employeeName.charAt(0)}
                    </Avatar>
                    <Box className={styles.employeeInfo}>
                      <Typography variant="h6" className={styles.employeeName}>
                        {employeeName}
                      </Typography>
                      <Typography variant="body2" className={styles.employeeId}>
                        ID: {employee_id}
                      </Typography>
                      <Typography
                        variant="body2"
                        className={styles.employeeDepartment}
                      >
                        {department}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider className={styles.divider} />

                  <Box className={styles.detailsSection}>
                    <Typography
                      variant="subtitle1"
                      className={styles.sectionTitle}
                    >
                      Leave Details
                    </Typography>

                    <Grid container spacing={3} className={styles.detailsGrid}>
                      <Grid item xs={12} sm={6}>
                        <Box className={styles.detailItem}>
                          <Box className={styles.detailIcon}>
                            <Description />
                          </Box>
                          <Box>
                            <Typography
                              variant="body2"
                              className={styles.detailLabel}
                            >
                              Leave Type
                            </Typography>
                            <Typography
                              variant="body1"
                              className={styles.detailValue}
                            >
                              {leave_type}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box className={styles.detailItem}>
                          <Box className={styles.detailIcon}>
                            <AccessTime />
                          </Box>
                          <Box>
                            <Typography
                              variant="body2"
                              className={styles.detailLabel}
                            >
                              Duration
                            </Typography>
                            <Typography
                              variant="body1"
                              className={styles.detailValue}
                            >
                              {days_requested} days
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box className={styles.detailItem}>
                          <Box className={styles.detailIcon}>
                            <CalendarToday />
                          </Box>
                          <Box>
                            <Typography
                              variant="body2"
                              className={styles.detailLabel}
                            >
                              Start Date
                            </Typography>
                            <Typography
                              variant="body1"
                              className={styles.detailValue}
                            >
                              {formatDate(start_date)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box className={styles.detailItem}>
                          <Box className={styles.detailIcon}>
                            <CalendarToday />
                          </Box>
                          <Box>
                            <Typography
                              variant="body2"
                              className={styles.detailLabel}
                            >
                              End Date
                            </Typography>
                            <Typography
                              variant="body1"
                              className={styles.detailValue}
                            >
                              {formatDate(end_date)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box className={styles.detailItem}>
                          <Box className={styles.detailIcon}>
                            <Person />
                          </Box>
                          <Box>
                            <Typography
                              variant="body2"
                              className={styles.detailLabel}
                            >
                              Relief Person
                            </Typography>
                            <Typography
                              variant="body1"
                              className={styles.detailValue}
                            >
                              {reliefPersonName}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box className={styles.detailItem}>
                          <Box className={styles.detailIcon}>
                            <Phone />
                          </Box>
                          <Box>
                            <Typography
                              variant="body2"
                              className={styles.detailLabel}
                            >
                              Contact During Leave
                            </Typography>
                            <Typography
                              variant="body1"
                              className={styles.detailValue}
                            >
                              {contactInfo}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        <Box className={styles.detailItem}>
                          <Box className={styles.detailIcon}>
                            <LocationOn />
                          </Box>
                          <Box className={styles.fullWidthDetail}>
                            <Typography
                              variant="body2"
                              className={styles.detailLabel}
                            >
                              Location During Leave
                            </Typography>
                            <Typography
                              variant="body1"
                              className={styles.detailValue}
                            >
                              {location}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>

                  <Divider className={styles.divider} />

                  <Box className={styles.reasonSection}>
                    <Typography
                      variant="subtitle1"
                      className={styles.sectionTitle}
                    >
                      Reason for Leave
                    </Typography>
                    <Paper variant="outlined" className={styles.reasonPaper}>
                      <Typography variant="body1" className={styles.reasonText}>
                        {reason}
                      </Typography>
                    </Paper>
                  </Box>

                  {documents && documents.length > 0 && (
                    <>
                      <Divider className={styles.divider} />
                      <Box className={styles.documentsSection}>
                        <Typography
                          variant="subtitle1"
                          className={styles.sectionTitle}
                        >
                          Supporting Documents
                        </Typography>
                        <Box className={styles.documentsList}>
                          {documents.map((doc, index) => (
                            <Paper
                              key={index}
                              variant="outlined"
                              className={styles.documentItem}
                            >
                              <Box className={styles.documentInfo}>
                                <InsertDriveFile
                                  className={styles.documentIcon}
                                />
                                <Typography
                                  variant="body2"
                                  className={styles.documentName}
                                >
                                  {doc.name}
                                </Typography>
                              </Box>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Download />}
                                href={doc.url}
                                download
                                className={styles.downloadButton}
                              >
                                Download
                              </Button>
                            </Paper>
                          ))}
                        </Box>
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Box className={styles.sidebarContainer}>
                <Card elevation={0} className={styles.balanceCard}>
                  <CardHeader
                    title={
                      <Typography variant="h6" className={styles.cardTitle}>
                        Leave Balance
                      </Typography>
                    }
                    className={styles.cardHeader}
                  />
                  <CardContent className={styles.balanceContent}>
                    <Box className={styles.balanceCircleContainer}>
                      <Box className={styles.balanceCircle}>
                        <Typography
                          variant="h4"
                          className={styles.remainingDaysValue}
                        >
                          {remainingDays}
                        </Typography>
                        <Typography
                          variant="body2"
                          className={styles.remainingDaysLabel}
                        >
                          days remaining
                        </Typography>
                      </Box>
                    </Box>

                    <Box className={styles.balanceDetails}>
                      <Box className={styles.balanceItem}>
                        <Typography
                          variant="body2"
                          className={styles.balanceLabel}
                        >
                          Total Allocation
                        </Typography>
                        <Typography
                          variant="h6"
                          className={styles.balanceValue}
                        >
                          {totalDays} days
                        </Typography>
                      </Box>

                      <Box className={styles.balanceItem}>
                        <Typography
                          variant="body2"
                          className={styles.balanceLabel}
                        >
                          Used
                        </Typography>
                        <Typography
                          variant="h6"
                          className={styles.balanceValue}
                        >
                          {usedDays} days
                        </Typography>
                      </Box>

                      <Box className={styles.progressContainer}>
                        <Box className={styles.progressLabels}>
                          <Typography
                            variant="caption"
                            className={styles.progressLabel}
                          >
                            {usagePercentage.toFixed(0)}% Used
                          </Typography>
                          <Typography
                            variant="caption"
                            className={styles.progressLabel}
                          >
                            {(100 - usagePercentage).toFixed(0)}% Remaining
                          </Typography>
                        </Box>
                        <Box className={styles.progressBarContainer}>
                          <Box
                            className={styles.progressBarFill}
                            style={{ width: `${usagePercentage}%` }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                {status === "pending" && userRole?.roleID != "41" && (
                  <Card elevation={0} className={styles.actionsCard}>
                    <CardHeader
                      title={
                        <Typography variant="h6" className={styles.cardTitle}>
                          Actions
                        </Typography>
                      }
                      className={styles.cardHeader}
                    />
                    <CardContent>
                      <LeaveStatusActions
                        leaveId={leaveId || ""}
                        employeeId={employee_id}
                      />
                    </CardContent>
                  </Card>
                )}
              </Box>
            </Grid>
          </Grid>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default LeaveDetailPage;
